/**
 * Таймфреймы и арифметика баров.
 *
 * Живёт в контрактах, а не в Core, потому что это свойство самих данных:
 * граница часового бара одна и та же для сервера, терминала и бота.
 * Если бы каждый считал её сам, «одна и та же свеча для всех потребителей»
 * (ТЗ, раздел 8.3) держалась бы на честном слове.
 */
/*
 * Порядок здесь — от младшего к старшему, и на него опираются: выбор
 * ближайшего доступного таймфрейма идёт по этому списку.
 *
 * Двух- и трёхминутных баров у бирж обычно нет — их собирает Core из
 * минутных, как и трёхчасовые из часовых. Для потребителя разницы нет:
 * он просит таймфрейм, а откуда тот взялся, решает `resampleSource`.
 */
export const TIMEFRAMES = [
    '1m',
    '2m',
    '3m',
    '5m',
    '15m',
    '30m',
    '1H',
    '2H',
    '3H',
    '4H',
    '6H',
    '12H',
    '1D',
    '3D',
    '1W',
    // Месяц — календарный: свеча с 1-го числа 00:00 UTC, длина 28–31 сутки (владелец 01.11.2026).
    '1M',
];
export function isTimeframe(value) {
    return typeof value === 'string' && TIMEFRAMES.includes(value);
}
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
/**
 * 1970-01-01 — четверг, то есть день 0 недельной сетки эпохи приходится
 * на четверг. Ближайший понедельник наступает через 4 дня, поэтому сетка
 * недельных баров сдвинута на +4 дня относительно эпохи.
 */
const WEEK_OFFSET = 4 * DAY;
/**
 * Сетка трёхдневных баров сдвинута на сутки от эпохи — так их ставит
 * Binance: время открытия его 3д-свечи даёт в остатке от трёх суток
 * ровно одни сутки. Своя сетка от эпохи разошлась бы с биржей на день,
 * и собранные из суток бары Bybit легли бы не туда, где у Binance.
 */
const THREE_DAY_OFFSET = DAY;
const DURATION = {
    '1m': MINUTE,
    '2m': 2 * MINUTE,
    '3m': 3 * MINUTE,
    '5m': 5 * MINUTE,
    '15m': 15 * MINUTE,
    '30m': 30 * MINUTE,
    '1H': HOUR,
    '2H': 2 * HOUR,
    '3H': 3 * HOUR,
    '4H': 4 * HOUR,
    '6H': 6 * HOUR,
    '12H': 12 * HOUR,
    '1D': DAY,
    '3D': 3 * DAY,
    '1W': WEEK,
    // Средняя длина месяца: для оценок «сколько баров в окне». Сетка месяца считается по календарю.
    '1M': 30 * DAY,
};
export function timeframeMs(tf) {
    return DURATION[tf];
}
/** Время открытия бара, которому принадлежит момент `ts`. Всегда UTC. */
export function alignToTimeframe(ts, tf) {
    if (tf === '1M') {
        const d = new Date(ts);
        return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
    }
    const size = DURATION[tf];
    if (tf === '1W') {
        return Math.floor((ts - WEEK_OFFSET) / size) * size + WEEK_OFFSET;
    }
    if (tf === '3D') {
        return Math.floor((ts - THREE_DAY_OFFSET) / size) * size + THREE_DAY_OFFSET;
    }
    return Math.floor(ts / size) * size;
}
export function isAligned(ts, tf) {
    return alignToTimeframe(ts, tf) === ts;
}
export function nextBarTime(barTime, tf) {
    if (tf === '1M') {
        const d = new Date(barTime);
        return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    }
    return barTime + DURATION[tf];
}
/** Число баров в полуинтервале [from, to). Отрицательным не бывает. */
export function barsBetween(from, to, tf) {
    if (to <= from)
        return 0;
    if (tf === '1M') {
        const a = new Date(from);
        const b = new Date(to);
        return (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
    }
    return Math.round((to - from) / DURATION[tf]);
}
/**
 * Можно ли собрать `target` ресемплингом из `source`.
 *
 * Требуется не только кратность длительностей, но и совпадение сетки:
 * бар источника не должен пересекать границу целевого бара.
 */
export function canResample(source, target) {
    const s = DURATION[source];
    const t = DURATION[target];
    // Месяц собирается из того, что делит сутки нацело: границы недель и трёхдневок с месяцем не совпадают.
    if (target === '1M')
        return source !== '1M' && s <= DAY && DAY % s === 0;
    if (source === '1M')
        return false;
    if (s >= t || t % s !== 0)
        return false;
    // 1W выровнен по понедельнику, остальные — по эпохе. Сетки сходятся,
    // потому что все шаги младше суток делят сутки нацело.
    return DAY % s === 0 || t % s === 0;
}
/**
 * Наименьший таймфрейм из числа доступных, из которого можно собрать `target`.
 * Возвращает сам `target`, если он доступен нативно.
 */
export function resampleSource(target, available) {
    if (available.includes(target))
        return target;
    const candidates = TIMEFRAMES.filter((tf) => available.includes(tf) && canResample(tf, target));
    if (candidates.length === 0)
        return null;
    // Самый крупный из подходящих: меньше баров тянуть с биржи.
    return candidates.reduce((best, tf) => (DURATION[tf] > DURATION[best] ? tf : best));
}
//# sourceMappingURL=timeframe.js.map